import clsx from 'clsx';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { forwardRef, useCallback, useRef, useState } from 'react';
import { Map, Marker, NavigationControl, Popup, ScaleControl } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { Crop } from '../../assets/customIcons/Crop';
import { Measure } from '../../assets/customIcons/Measure';
import { LAYER_CONFIGS, vectorTileLayerIds } from '../../constants/general';
import { useDataLoadMonitor } from '../../hooks/map/useDataLoadMonitor';
import { setCropBounds as setCropBoundsAction, setZoom } from '../../store/actions/map';
import { filterSelector } from '../../store/selectors/filters';
import { basemapSelector, selectedAddressLocationSelector } from '../../store/selectors/general';
import { registerMapIcons } from '../../utils/mapIcons';
import { AddressSearch } from './AddressSearch';
import { BasemapOptions } from './BasemapOptions';
import { CropTool } from './CropTool';
import { DataLoadIndicator } from './DataLoadIndicator';
import { DistanceMeasureTool } from './DistanceMeasureTool';
import { LayerRenderer } from './LayerRenderer';
import { MapAnchors } from './MapAnchors';
import { StructureLegend } from './StructureLegend';
import { formatTooltipContent, interactiveLayerIds, onLayerClick } from './Tooltips';
import { VectorTileRenderer } from './VectorTileRenderer';
import { ZoomOnLoad } from './ZoomOnLoad';
import { ZoomToCounty } from './ZoomToCounty';
import { trackEvent, MAP_CROP_MODE } from '../../utils/googleAnalytics';

// react-map-gl's default `require('mapbox-gl')` can resolve to a broken shape under CRA; pass the resolved API explicitly.
mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_KEY;

const WINDOW_HEIGHT = window.innerHeight;
const WINDOW_HEIGHT_DIFFERENCE = '200px';

const BASEMAP_STYLES = {
  light: 'mapbox://styles/mapbox/light-v11',
  dark: 'mapbox://styles/mapbox/dark-v11',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
};

export const FloodMap = forwardRef(
  ({ boundaryData, layerManager, isBoundaryDataFetching, activeTab }, ref) => {
    const { layers, layerVisibility, layerOpacity } = layerManager;

    const dispatch = useDispatch();
    const selectedAddressLocation = useSelector(selectedAddressLocationSelector);
    const basemapInfo = useSelector(basemapSelector);
    const filterInfo = useSelector(filterSelector);
    const [measureMode, setMeasureMode] = useState(false);
    const [cropMode, setCropMode] = useState(false);
    const [cropResetTrigger, setCropResetTrigger] = useState(0);
    const [isAddressValidating, setIsAddressValidating] = useState(false);
    const [hoverInfo, setHoverInfo] = useState(null);
    const [currentZoom, setCurrentZoom] = useState(10);

    // Internal map ref
    const mapRef = useRef(null);

    const handleMapLoad = useCallback(
      e => {
        registerMapIcons(e.target);

        // Set initial zoom level when map loads
        const initialZoom = e.target.getZoom();
        dispatch(setZoom(initialZoom));
        setCurrentZoom(initialZoom);
      },
      [dispatch],
    );

    const handleZoomChange = useCallback(
      e => {
        const newZoom = e.viewState.zoom;
        setCurrentZoom(newZoom);
        dispatch(setZoom(newZoom));
      },
      [dispatch],
    );

    // Monitor data load
    const { dataLoadInfo } = useDataLoadMonitor({
      layers,
      layerVisibility,
      zoom: Math.round(currentZoom),
    });

    const handleMapRef = node => {
      mapRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    const visibleLayers = Object.entries(layerVisibility).filter(([, isVisible]) => isVisible);

    const visibleVectorTileLayers = Object.fromEntries(
      Object.entries(layerVisibility).filter(
        ([key, value]) => vectorTileLayerIds.includes(key) && value,
      ),
    );

    const mapStyle = BASEMAP_STYLES[basemapInfo] || BASEMAP_STYLES.light;

    return (
      <div
        className="relative h-full w-full"
        style={{ height: `calc(${WINDOW_HEIGHT}px - ${WINDOW_HEIGHT_DIFFERENCE})` }}
      >
        <div className="relative h-full">
          <BasemapOptions />
          {isAddressValidating && (
            <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white bg-opacity-70">
              <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-teal" />
              <p className="ml-4 text-lg font-medium">Loading address...</p>
            </div>
          )}

          <div className="absolute right-2 top-3 z-[1000] flex flex-col items-end gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => {
                  trackEvent(MAP_CROP_MODE, { cropMode: !cropMode });

                  const nextCropMode = !cropMode;
                  setCropMode(nextCropMode);
                  if (nextCropMode) {
                    setCropResetTrigger(prev => prev + 1);
                  } else {
                    dispatch(setCropBoundsAction(null));
                  }
                }}
                className={clsx(
                  'flex items-center justify-center gap-2 rounded-sm border-2 border-yellow px-3 py-2 text-sm font-medium shadow-md transition-colors hover:brightness-105',
                  cropMode
                    ? 'bg-yellow text-gray-900 hover:brightness-105'
                    : 'bg-white text-gray-700',
                )}
              >
                Export map
                <Crop className="h-[18px] w-[18px] shrink-0" />
              </button>
              <button
                onClick={() => setMeasureMode(!measureMode)}
                className={clsx(
                  'w-fit rounded border border-blue px-2 py-1 font-medium shadow-lg transition-colors',
                  measureMode
                    ? 'hover:white bg-blue stroke-white text-white'
                    : 'hover:slateBlue bg-white text-gray-700',
                )}
              >
                <Measure className={clsx('h-8 w-8', measureMode && 'text-white')} />
              </button>
            </div>
            {measureMode && (
              <div className="whitespace-nowrap rounded border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 shadow-lg">
                <b>Double-click</b> two points on the map to measure distance
              </div>
            )}
          </div>

          <Map
            ref={handleMapRef}
            style={{ height: '100%', width: '100%' }}
            mapStyle={mapStyle}
            maxZoom={18}
            scrollZoom
            doubleClickZoom={false}
            preserveDrawingBuffer={true}
            onLoad={handleMapLoad}
            onZoom={handleZoomChange}
            interactiveLayerIds={interactiveLayerIds(visibleVectorTileLayers, layerVisibility)}
            onClick={onLayerClick(setHoverInfo)}
          >
            <MapAnchors />
            <NavigationControl position="top-left" visualizePitch />
            <ScaleControl unit="imperial" />
            <AddressSearch
              boundaryData={boundaryData}
              onValidatingChange={setIsAddressValidating}
            />
            <DistanceMeasureTool
              isActive={activeTab === 'map' && measureMode}
              onMeasure={msg => console.log(msg)}
            />
            <CropTool
              isActive={cropMode}
              resetTrigger={cropResetTrigger}
              onClose={() => {
                setCropMode(false);
                dispatch(setCropBoundsAction(null));
              }}
            />
            <ZoomOnLoad
              address={selectedAddressLocation}
              center={[-99.254, 31.348]}
              boundaryData={boundaryData}
            />

            {boundaryData && <ZoomToCounty boundaryData={boundaryData} filterInfo={filterInfo} />}
            {selectedAddressLocation && (
              <Marker
                longitude={selectedAddressLocation.lon}
                latitude={selectedAddressLocation.lat}
              />
            )}

            {boundaryData?.areaData && filterInfo.selectedCounty && (
              <LayerRenderer
                layerId="boundary"
                data={boundaryData.areaData.geoJson}
                isVisible={true}
                opacity={1}
              />
            )}

            {filterInfo.selectedCounty &&
              Object.entries(visibleVectorTileLayers).map(([layerId]) => (
                <VectorTileRenderer
                  key={layerId}
                  layerId={layerId}
                  filterInfo={filterInfo}
                  config={LAYER_CONFIGS[layerId]}
                  isVisible={layerVisibility[layerId]}
                  opacity={layerOpacity[layerId]}
                />
              ))}

            {layers &&
              filterInfo.selectedCounty &&
              Object.entries(layers).map(([layerId, data]) => (
                <LayerRenderer
                  key={layerId}
                  layerId={layerId}
                  data={data}
                  isVisible={layerVisibility[layerId]}
                  opacity={layerOpacity[layerId]}
                  measureMode={measureMode}
                />
              ))}

            {hoverInfo &&
              (() => {
                // check if there's any tooltip info to display and if not, don't display the popup
                const tooltipContent = formatTooltipContent(hoverInfo.feature);
                if (!tooltipContent) return null;

                return (
                  <Popup
                    longitude={hoverInfo.longitude}
                    latitude={hoverInfo.latitude}
                    closeButton={true}
                    closeOnClick={false}
                    onClose={() => setHoverInfo(null)}
                    anchor="bottom"
                    offset={10}
                    className="tooltip-popup"
                    maxWidth="325px"
                  >
                    {tooltipContent}
                  </Popup>
                );
              })()}
          </Map>

          {Object.keys(visibleLayers).length > 0 && (
            <StructureLegend layers={layers} layerVis={layerVisibility} />
          )}

          <DataLoadIndicator dataLoadInfo={dataLoadInfo} />

          {isBoundaryDataFetching && (
            <div className="absolute inset-0 z-[9999] flex items-center justify-center bg-white bg-opacity-70">
              <div className="h-16 w-16 animate-spin rounded-full border-b-4 border-t-4 border-teal" />
              <p className="ml-4 text-lg font-medium">Loading... This may take a moment.</p>
            </div>
          )}
        </div>
      </div>
    );
  },
);
